import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { getChatDetail, updateStatusMessage } from "slices/chat/thunk";
import { getDetailContact } from "slices/contact/thunk";
import { cn } from "lib/utils";

import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";

const Conversation = () => {
  document.title = `Chat | EngageHub IQ`;
  const dispatch = useDispatch<any>();
  const chatDetail = useSelector((state: any) => state.Chat.chatDetail);
  const contactDetail = useSelector(
    (state: any) => state.Contact.contactDetail
  );
  const [showContactInfo, setShowContactInfo] = React.useState(false);
  const [showActiveChat, setShowActiveChat] = React.useState(false);
  const [isMessageDeleted, setIsMessageDeleted] = React.useState(false);
  const [showChatList, setShowChatList] = React.useState(true);
  const [loadingChatDetail, setLoadingChatDetail] = React.useState(false);
  const location = useLocation();

  const { fromChatOpen, conversationId, contactId } =
    (location.state as {
      fromChatOpen?: boolean;
      conversationId?: string;
      contactId?: string;
    }) || {};

  const navigate = useNavigate();
  useEffect(() => navigate("/conversation"), [navigate]);

  useEffect(() => {
    if (!fromChatOpen) return;
    setShowActiveChat(true);
    setShowContactInfo(true);
    if (window.innerWidth <= 1280) setShowChatList(false);

    const load = async () => {
      setLoadingChatDetail(true);
      try {
        await dispatch(getChatDetail(conversationId!));
      } finally {
        setLoadingChatDetail(false);
      }
    };
    load();
  }, [fromChatOpen, conversationId, contactId, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) setShowChatList(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChatSelect = async (chat: any) => {
    setLoadingChatDetail(true);

    if (
      chat?.contactId !== contactDetail?.id &&
      contactDetail?.id !== chat?.contactId
    ) {
      await dispatch(getDetailContact(chat.contactId));
    }

    const detail = await dispatch(getChatDetail(chat.id)).unwrap();
    if (detail?.messages?.length > 0) {
      const lastMessage = detail.messages[detail.messages.length - 1];
      await dispatch(updateStatusMessage(lastMessage.id));
    }

    setIsMessageDeleted(false);
    setShowActiveChat(true);
    setShowContactInfo(true);

    if (window.innerWidth < 1280) setShowChatList(false);
    setLoadingChatDetail(false);
  };

  const handleBackToChatList = () => {
    setShowChatList(true);
    setShowActiveChat(false);
    setShowContactInfo(false);
  };

  const showMessagesPanel =
    !isMessageDeleted &&
    showContactInfo &&
    (chatDetail?.id || contactDetail?.id);

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8">
      <div
        className={cn(
          "flex h-[calc(100vh-4rem)] w-full overflow-hidden",
          "border-y border-border/60 bg-card/30 backdrop-blur-sm xl:border xl:rounded-2xl"
        )}
      >
        {/* Chat list */}
        <div
          className={cn(
            "xl:block xl:w-80 xl:shrink-0 xl:border-r xl:border-border/60",
            showChatList ? "block w-full" : "hidden"
          )}
        >
          <ChatList
            onChatSelect={handleChatSelect}
            selectedChatId={(showActiveChat && chatDetail?.id) || null}
          />
        </div>

        {/* Chat messages */}
        {showMessagesPanel ? (
          <div
            className={cn(
              "min-w-0 flex-1",
              !showChatList ? "block w-full" : "hidden xl:block"
            )}
          >
            <ChatMessages
              loadingChatDetail={loadingChatDetail}
              onChatDeleted={() => {
                setIsMessageDeleted(true);
                handleBackToChatList();
              }}
              chatDetail={chatDetail}
              contactDetail={contactDetail}
              onBackToChatList={handleBackToChatList}
            />
          </div>
        ) : (
          <div className="hidden flex-1 items-center justify-center text-center xl:flex">
            <div className="max-w-sm space-y-3 px-6">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-foreground">
                Select a conversation
              </h3>
              <p className="text-sm text-muted-foreground">
                Pick a chat from the list to view messages and reply.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Conversation;
